import {
  Answer,
  AnswerWithQid,
  AnswerWithScore,
  Question,
  QuestionWithScore,
  User,
} from '../../../types';
import { AWARDS } from '../baseComponents/award';

const WEEKSSINCEEPOCH = new Date().getTime() / (7 * 24 * 60 * 60 * 1000);
const SECONDSTILLWEEKAGO = (WEEKSSINCEEPOCH - 1) * (7 * 24 * 60 * 60);

/**
 * Function to get the relevance of a time in the last week out of 10.
 * Returns 0 if the time was a week ago or more.
 *
 * @param time - The time to get the relevance of.
 * @returns the relevance.
 */
const getRelevance = (time: Date) => {
  const secondsTillTime = new Date(time).getTime() / 1000;
  const secondsTillNow = new Date().getTime() / 1000;
  const ret = Math.max(
    0,
    Math.round((secondsTillTime - SECONDSTILLWEEKAGO) / (secondsTillNow - SECONDSTILLWEEKAGO)) * 10,
  );
  return ret;
};

/**
 * Function to get an overall score for a given answer.
 *
 * @param answer - The answer to get the overall score for.
 * @returns the overall score.
 */
const getAnswerOverallScore = (answer: AnswerWithQid): number =>
  (answer.comments?.length || 0) + getRelevance(answer.ansDateTime);

/**
 * Function to get a trending score for a given answer.
 *
 * @param answer - The answer to get the trending score for.
 * @returns the trending score.
 */
const getAnswerTrendingScore = (answer: AnswerWithQid): number => {
  let score = 0;
  answer.comments.forEach(c => {
    score += getRelevance(c.commentDateTime);
  });
  return score;
};

/**
 * Function to get a list of answers with overall or trending scores.
 *
 * @param answers - The answers to get the overall or trending scores for.
 * @param type - The type of score to get.
 * @returns the list of answers with overall or trending scores.
 */
export const getAnswersWithScore = (
  answers: Answer[],
  type: 'overall' | 'trending',
): AnswerWithScore[] =>
  answers.map(answer => ({
    ...answer,
    score: type === 'overall' ? getAnswerOverallScore(answer) : getAnswerTrendingScore(answer),
  }));

/**
 * Function to get an overall score for a given question.
 *
 * @param question - The question to get the overall score for.
 * @returns the overall score.
 */
const getQuestionOverallScore = (question: Question): number =>
  (question.answers?.length || 0) +
  (question.upVotes?.length || 0) -
  (question.downVotes?.length || 0) +
  (question.comments?.length || 0) +
  getRelevance(question.askDateTime);

/**
 * Function to get a trending score for a given question.
 *
 * @param question - The question to get the trending score for.
 * @returns the trending score.
 */
const getQuestionTrendingScore = (question: Question): number => {
  let score = 0;
  question.answers.forEach(a => {
    score += getRelevance(a.ansDateTime) + getAnswerTrendingScore(a);
  });
  question.comments.forEach(c => {
    score += getRelevance(c.commentDateTime);
  });
  return score;
};

/**
 * Function to get a list of questions with overall or trending scores.
 *
 * @param questions - The questions to get the overall or trending scores for.
 * @param type - The type of score to get.
 * @returns the list of questions with overall or trending scores.
 */
export const getQuestionsWithScore = (
  questions: Question[],
  type: 'overall' | 'trending',
): QuestionWithScore[] =>
  questions.map(question => ({
    ...question,
    score:
      type === 'overall' ? getQuestionOverallScore(question) : getQuestionTrendingScore(question),
  }));

/**
 * Function to get an overall score for a given user.
 *
 * @param user - The user to get the overall score for.
 * @returns the overall score.
 */
export const getUserOverallScore = (user: User) => {
  let userUpvotes = 0;
  let userDownvotes = 0;
  let userQuestions = 0;
  let userAnswers = 0;
  user.questions.forEach(q => {
    userUpvotes += q.upVotes?.length || 0;
    userDownvotes += q.downVotes?.length || 0;
    userQuestions += getQuestionOverallScore(q);
  });
  user.answers.forEach(a => {
    userAnswers += getAnswerOverallScore(a);
  });
  const awardsScore = user.awards.reduce(
    (total, award_name) =>
      total + (AWARDS.find(award => award.key === award_name)?.value.points || 0),
    0,
  );

  const achievementScore = user.achievements.reduce((total, achievement) => {
    if (achievement.awarded === true) {
      return total + achievement.points;
    }
    return total;
  }, 0);
  return user
    ? userQuestions +
        userAnswers +
        userUpvotes -
        userDownvotes +
        user.followers.length +
        achievementScore +
        awardsScore +
        user.score
    : 0;
};

/**
 * Function to get a trending score for a given user.
 *
 * @param user - The user to get the trending score for.
 * @returns the trending score.
 */
const getUserTrendingScore = (uid: string, questions: Question[], answers: AnswerWithQid[]) => {
  let score = 0;
  questions.forEach(q => {
    score += uid === q.askedByUser?._id ? getQuestionTrendingScore(q) : 0;
  });
  answers.forEach(a => {
    score += uid === a.ansByUser?._id ? getAnswerTrendingScore(a) : 0;
  });
  return score;
};

/**
 * Function to get a list of users with overall or trending scores.
 *
 * @param users - The users to get the overall or trending scores for.
 * @param type - The type of score to get.
 * @returns the list of users with overall or trending scores.
 */
export const getUsersWithUpdatedScore = (
  users: User[],
  questions: Question[],
  answers: AnswerWithQid[],
  type: 'overall' | 'trending',
): User[] =>
  users.map(user => ({
    ...user,
    score:
      type === 'overall'
        ? getUserOverallScore(user)
        : getUserTrendingScore(user._id || '', questions, answers),
  }));
